/**
 * The tenant's agent numbers and the salons each one serves.
 *
 * One component, three places: the company profile and both onboarding steps
 * (phone agent, WhatsApp agent). Keeping the salon picker, the number purchase
 * and the Meta connect flow in a single file is what stops the three screens
 * from drifting apart — the purchase in particular has a flag that must not be
 * forgotten (`purpose: 'additional_agent'`, see handleBuy).
 *
 * Data lives in `useAgentNumbers` so a page showing both channels loads the
 * salon list from TimeGlobe once instead of twice. The unsaved edits live
 * there too: every number is assigned in the card of each channel it answers,
 * and a line that answers both is the same agent in both cards.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Phone, Plus, Link2Off } from 'lucide-react';
import { WhatsAppIcon } from '../icons/WhatsAppIcon';
import WhatsAppVerification from '../steps/WhatsAppVerification';
import authService, { AgentNumberConfig } from '../../services/authService';
import twilioService, { TwilioAvailableNumber } from '../../services/twilioService';
import whatsappService, { MetaSignupData, WhatsAppMode } from '../../services/whatsappService';

export type AgentChannel = 'voice' | 'whatsapp';

interface Salon {
  siteCd: string;
  siteNm: string;
}

interface Draft {
  siteCds: string[];
  displayName: string;
}

/** Unsaved salon picks and greeting names, keyed by phone number. */
export type AgentNumberDrafts = Record<string, Draft>;

const errMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

// A salon that no longer exists in TimeGlobe has no button to untick it, and
// the backend refuses to store it again. It is left out of the draft, so the
// number shows as changed and the next save drops the stale entry.
const draftsFrom = (numbers: AgentNumberConfig[]): AgentNumberDrafts =>
  Object.fromEntries(
    numbers.map((n) => [
      n.phone_number,
      {
        siteCds: (n.site_cds || []).filter((cd) => !(n.unknown_site_cds || []).includes(cd)),
        displayName: n.display_name || '',
      },
    ]),
  );

/** Numbers + salons for the whole tenant, loaded once per page. */
export function useAgentNumbers() {
  const [numbers, setNumbers] = useState<AgentNumberConfig[]>([]);
  const [sites, setSites] = useState<Salon[]>([]);
  const [whatsappMode, setWhatsappMode] = useState<WhatsAppMode>('central');
  const [loading, setLoading] = useState(true);
  // Held here, not in the cards: a line that answers calls and WhatsApp is
  // listed in both, and the two must never show different picks for it.
  const [drafts, setDrafts] = useState<AgentNumberDrafts>({});

  const reload = useCallback(async () => {
    try {
      const res = await authService.getNumberConfigs();
      const loaded = res.numbers || [];
      setNumbers(loaded);
      // Server state is the source of truth; local edits only live until a
      // save or a reload replaces them. Set together with the numbers, so no
      // click can land between the list appearing and its drafts.
      setDrafts(draftsFrom(loaded));
      if (res.sites?.length) setSites(res.sites);
      setWhatsappMode(res.whatsapp_mode || 'central');
      return loaded;
    } catch (err) {
      console.error('Error fetching agent numbers:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { numbers, sites, whatsappMode, loading, reload, drafts, setDrafts };
}

interface Props {
  channel: AgentChannel;
  numbers: AgentNumberConfig[];
  sites: Salon[];
  loading?: boolean;
  /** Unsaved edits from `useAgentNumbers`, shared by every card on the page. */
  drafts: AgentNumberDrafts;
  onDraftsChange: React.Dispatch<React.SetStateAction<AgentNumberDrafts>>;
  /** Buy another Twilio number (phone agents only). */
  showBuy?: boolean;
  /** Connect / disconnect a WhatsApp account per number. */
  showWhatsAppConnect?: boolean;
  emptyHint?: string;
  onReload: () => Promise<unknown>;
}

const AgentNumberManager: React.FC<Props> = ({
  channel,
  numbers,
  sites,
  loading = false,
  drafts,
  onDraftsChange,
  showBuy = false,
  showWhatsAppConnect = false,
  emptyHint,
  onReload,
}) => {
  const [saving, setSaving] = useState<string | null>(null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [buyCountry, setBuyCountry] = useState<'DE' | 'CH' | 'AT'>('DE');
  const [buyCandidates, setBuyCandidates] = useState<TwilioAvailableNumber[]>([]);
  const [buySelected, setBuySelected] = useState('');
  const [buySearching, setBuySearching] = useState(false);
  const [buying, setBuying] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [authCode, setAuthCode] = useState<string | null>(null);
  const [signupData, setSignupData] = useState<MetaSignupData | null>(null);

  const [connectOpen, setConnectOpen] = useState(false);
  // '' = a line that does not run on our Twilio account, so Meta's code goes
  // straight to the customer's own phone and we have nothing to show.
  const [connectTarget, setConnectTarget] = useState<string>('');
  const [arming, setArming] = useState(false);
  // The number currently routed to the verification webhook. Held in a ref too,
  // because the unmount cleanup must restore it without re-running on rerenders.
  const [armedNumber, setArmedNumber] = useState<string | null>(null);
  const armedNumberRef = useRef<string | null>(null);

  const visible = numbers.filter((n) =>
    channel === 'voice'
      ? n.channel === 'voice' || n.channel === 'both'
      : n.channel === 'whatsapp' || n.channel === 'both',
  );

  // --- assignment -----------------------------------------------------------

  const toggleSite = (phone: string, siteCd: string) => {
    onDraftsChange((prev) => {
      const current = prev[phone] || { siteCds: [], displayName: '' };
      const next = current.siteCds.includes(siteCd)
        ? current.siteCds.filter((s) => s !== siteCd)
        : [...current.siteCds, siteCd];
      return { ...prev, [phone]: { ...current, siteCds: next } };
    });
  };

  const setDisplayName = (phone: string, value: string) => {
    onDraftsChange((prev) => ({
      ...prev,
      [phone]: { siteCds: prev[phone]?.siteCds || [], displayName: value },
    }));
  };

  const isDirty = (cfg: AgentNumberConfig): boolean => {
    const draft = drafts[cfg.phone_number];
    if (!draft) return false;
    const sameName = (draft.displayName || '') === (cfg.display_name || '');
    const assigned = cfg.site_cds || [];
    const sameSites =
      draft.siteCds.length === assigned.length &&
      draft.siteCds.every((s) => assigned.includes(s));
    return !(sameName && sameSites);
  };

  const handleSave = async (phone: string) => {
    const draft = drafts[phone];
    if (!draft) return;
    setSaving(phone);
    try {
      await authService.upsertNumberConfig(
        phone,
        draft.siteCds,
        draft.displayName.trim() || null,
      );
      await onReload();
      toast.success('Zuteilung gespeichert');
    } catch (err) {
      toast.error(errMessage(err, 'Zuteilung konnte nicht gespeichert werden'));
    } finally {
      setSaving(null);
    }
  };

  // --- buying another number ------------------------------------------------

  const handleSearch = async (country: 'DE' | 'CH' | 'AT') => {
    setBuySearching(true);
    setBuySelected('');
    try {
      const res = await twilioService.listAvailableNumbers({
        number_type: 'local',
        country,
        limit: 15,
      });
      setBuyCandidates(res.numbers || []);
      if (!res.numbers?.length) {
        toast.info(res.hint || 'Aktuell sind keine Nummern für dieses Land verfügbar.');
      }
    } catch (err) {
      toast.error(errMessage(err, 'Verfügbare Nummern konnten nicht geladen werden'));
      setBuyCandidates([]);
    } finally {
      setBuySearching(false);
    }
  };

  const handleBuy = async () => {
    if (!buySelected) return;
    setBuying(true);
    try {
      // AT-Fallback: Kandidaten können national (+43 720) sein — Kauf mit deren Typ.
      const candidate = buyCandidates.find((n) => n.phone_number === buySelected);
      await twilioService.purchaseNumber({
        phone_number: buySelected,
        number_type: (candidate?.number_type as 'local' | 'national' | undefined) || 'local',
        // Leaves the tenant's primary-number slot alone and wires the normal
        // voice webhook instead of the WhatsApp verification one.
        purpose: 'additional_agent',
      });
      await onReload();
      setBuyOpen(false);
      setBuyCandidates([]);
      setBuySelected('');
      toast.success('Rufnummer reserviert — jetzt Salons zuordnen');
    } catch (err) {
      toast.error(errMessage(err, 'Nummer konnte nicht gekauft werden'));
    } finally {
      setBuying(false);
    }
  };

  // --- connecting a further WhatsApp account --------------------------------
  //
  // Meta splits the result across two channels: the auth code arrives in the
  // FB.login callback, the phone/WABA ids through a window message. Only when
  // both are in can we call the backend.

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
        setSignupData({
          business_id: String(data.business_id || data.data?.business_id || ''),
          phone_number_id: String(data.phone_number_id || data.data?.phone_number_id || ''),
          waba_id: String(data.waba_id || data.data?.waba_id || ''),
          event: String(data.event || 'FINISH'),
        });
      } catch {
        /* not our message */
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Meta verifies the line by calling or texting it. On a Twilio line that call
  // would otherwise reach the voice agent, which would happily talk to Meta's
  // robot while the code is lost — so the line is routed to the transcribing
  // verification webhook for the duration of the connect, and handed back after.
  const releaseVerificationWebhook = useCallback(async () => {
    const phone = armedNumberRef.current;
    if (!phone) return;
    armedNumberRef.current = null;
    setArmedNumber(null);
    try {
      await twilioService.configureVoiceWebhook(phone);
    } catch (err) {
      // The backend restores abandoned numbers on its own after 20 minutes, so
      // this is recoverable — but the user should know the agent is still muted.
      console.error('Could not hand the line back to the voice agent:', err);
      toast.error(
        `${phone} nimmt gerade noch keine Anrufe entgegen. Bitte die Seite neu laden — ` +
          'sonst schaltet das System die Nummer selbst zurück.',
      );
    }
  }, []);

  useEffect(() => () => {
    // Tab closed / navigated away mid-flow: fire and forget, the backend sweep
    // covers the case where this never reaches the server.
    void releaseVerificationWebhook();
  }, [releaseVerificationWebhook]);

  useEffect(() => {
    if (!authCode || !signupData || connecting) return;
    const run = async () => {
      setConnecting(true);
      try {
        const res = await whatsappService.connectAdditionalNumber(authCode, signupData);
        await releaseVerificationWebhook();
        await onReload();
        setConnectOpen(false);
        toast.success(res.message || 'WhatsApp-Nummer verbunden');
      } catch (err) {
        toast.error(errMessage(err, 'WhatsApp-Nummer konnte nicht verbunden werden'));
      } finally {
        setAuthCode(null);
        setSignupData(null);
        setConnecting(false);
      }
    };
    run();
  }, [authCode, signupData, connecting, onReload, releaseVerificationWebhook]);

  const openMetaSignup = () => {
    const FB = (window as any).FB;
    if (!FB) {
      toast.error('Facebook-SDK nicht geladen. Bitte die Seite neu laden.');
      return;
    }
    setAuthCode(null);
    setSignupData(null);
    FB.login(
      (response: any) => {
        const code =
          response?.authResponse?.code ??
          response?.authResponse?.data?.code ??
          response?.code;
        if (code) {
          setAuthCode(String(code));
        } else {
          toast.error(
            'Facebook-Login nicht abgeschlossen. Falls Meta eine Sicherheitsabfrage ' +
              'verlangt hat, bitte auf facebook.com einloggen und erneut versuchen.',
          );
        }
      },
      {
        config_id: '966700112247031',
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      },
    );
  };

  // Lines we could connect: ours at Twilio and not yet doing WhatsApp. A number
  // already on "whatsapp" or "both" either is the main account or has its own.
  const connectable = numbers.filter((n) => n.is_twilio && n.channel === 'voice');

  const openConnectPanel = () => {
    setConnectTarget(connectable[0]?.phone_number || '');
    setConnectOpen(true);
  };

  const cancelConnect = async () => {
    setConnectOpen(false);
    await releaseVerificationWebhook();
  };

  const startWhatsAppConnect = async () => {
    // A line outside our Twilio account: Meta reaches the customer's own phone,
    // there is no webhook to swap and no code for us to display.
    if (!connectTarget) {
      openMetaSignup();
      return;
    }

    setArming(true);
    try {
      await twilioService.configureVerificationWebhook(connectTarget);
      armedNumberRef.current = connectTarget;
      setArmedNumber(connectTarget);
    } catch (err) {
      toast.error(
        errMessage(
          err,
          'Die Nummer konnte nicht auf Verifizierung umgestellt werden — bitte erneut versuchen.',
        ),
      );
      return;
    } finally {
      setArming(false);
    }

    openMetaSignup();
  };

  const handleDisconnect = async (phone: string) => {
    setDisconnecting(phone);
    try {
      const res = await whatsappService.disconnectAgentNumber(phone);
      await onReload();
      toast.success(res.message || 'WhatsApp-Anbindung getrennt');
    } catch (err) {
      toast.error(errMessage(err, 'Anbindung konnte nicht getrennt werden'));
    } finally {
      setDisconnecting(null);
    }
  };

  // --- render ---------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Rufnummern werden geladen…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {visible.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {emptyHint ||
            (channel === 'voice'
              ? 'Noch keine Rufnummer für den Telefonagenten verbunden.'
              : 'Noch keine WhatsApp-Nummer verbunden.')}
        </p>
      )}

      {visible.length > 0 && sites.length <= 1 && (
        <p className="text-sm text-muted-foreground">
          Dein Konto hat aktuell einen Salon. Eine Zuteilung lohnt sich ab dem zweiten
          Standort – bis dahin betreut jede Nummer diesen einen Salon.
        </p>
      )}

      {visible.map((cfg) => {
        const draft = drafts[cfg.phone_number] || { siteCds: [], displayName: '' };
        const channelLabel =
          cfg.channel === 'whatsapp'
            ? 'WhatsApp'
            : cfg.channel === 'both'
              ? 'Telefon + WhatsApp'
              : 'Telefon';
        const canAssign = sites.length > 1;
        // The name is what the phone agent greets with; WhatsApp does not use it.
        const showName = channel === 'voice';
        const alsoTakesCalls = channel === 'whatsapp' && cfg.channel === 'both';
        return (
          <div
            key={cfg.phone_number}
            className="p-4 bg-muted rounded-md border border-border space-y-4"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm font-medium text-foreground">
                {cfg.phone_number}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-background border border-border text-muted-foreground">
                {channelLabel}
              </span>
              {draft.siteCds.length === 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-background border border-border text-muted-foreground">
                  Alle Salons
                </span>
              )}
              {showWhatsAppConnect && cfg.has_own_whatsapp && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-muted-foreground hover:text-destructive-strong"
                  onClick={() => handleDisconnect(cfg.phone_number)}
                  disabled={disconnecting === cfg.phone_number}
                >
                  {disconnecting === cfg.phone_number ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Link2Off className="h-4 w-4 mr-2" />
                  )}
                  Verbindung trennen
                </Button>
              )}
            </div>

            {canAssign && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  Zuständige Salons
                </Label>
                <div className="flex flex-wrap gap-2">
                  {sites.map((site) => {
                    const active = draft.siteCds.includes(site.siteCd);
                    return (
                      <button
                        key={site.siteCd}
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleSite(cfg.phone_number, site.siteCd)}
                        className={
                          active
                            ? 'text-sm px-3 py-1.5 rounded-full border border-[#1967D2] bg-[#1967D2] text-white transition-colors'
                            : 'text-sm px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground transition-colors hover:border-[#1967D2] hover:text-foreground'
                        }
                      >
                        {site.siteNm}
                      </button>
                    );
                  })}
                </div>
                {alsoTakesCalls && (
                  <p className="text-xs text-muted-foreground">
                    Diese Nummer nimmt auch Anrufe an – die Auswahl gilt für Telefon und
                    WhatsApp.
                  </p>
                )}
                {cfg.unknown_site_cds.length > 0 && (
                  <p className="text-xs text-warning-strong">
                    Nicht mehr in TimeGlobe vorhanden: {cfg.unknown_site_cds.join(', ')} –
                    wird beim Speichern entfernt.
                  </p>
                )}
              </div>
            )}

            {(showName || canAssign) && (
              <div className="flex flex-wrap items-end gap-3">
                {showName && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                      Begrüßungsname (optional)
                    </Label>
                    <Input
                      value={draft.displayName}
                      onChange={(e) => setDisplayName(cfg.phone_number, e.target.value)}
                      placeholder="z. B. Filiale Süd"
                      className="w-64 bg-background border-border"
                    />
                  </div>
                )}
                <Button
                  onClick={() => handleSave(cfg.phone_number)}
                  disabled={saving === cfg.phone_number || !isDirty(cfg)}
                  className="bg-[#1967D2] hover:bg-[#1558b0]"
                >
                  {saving === cfg.phone_number && (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  )}
                  Speichern
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {showWhatsAppConnect && (
        <div className="pt-4 border-t border-border space-y-4">
          {!connectOpen ? (
            <div>
              <Button variant="outline" onClick={openConnectPanel} className="border-dashed">
                <WhatsAppIcon className="h-4 w-4 mr-2" />
                Weitere WhatsApp-Nummer verbinden
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Öffnet das Meta-Fenster wie bei der Einrichtung. Die bestehende
                WhatsApp-Verbindung bleibt unverändert.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  Welche Nummer soll WhatsApp bekommen?
                </Label>
                <div className="flex flex-wrap gap-2">
                  {connectable.map((n) => (
                    <button
                      key={n.phone_number}
                      type="button"
                      disabled={!!armedNumber}
                      onClick={() => setConnectTarget(n.phone_number)}
                      className={
                        connectTarget === n.phone_number
                          ? 'font-mono text-sm px-3 py-1.5 rounded-full border border-[#1967D2] bg-[#1967D2] text-white disabled:opacity-70 disabled:cursor-default'
                          : 'font-mono text-sm px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground enabled:hover:border-[#1967D2] enabled:hover:text-foreground transition-colors disabled:opacity-70 disabled:cursor-default'
                      }
                    >
                      {n.phone_number}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={!!armedNumber}
                    onClick={() => setConnectTarget('')}
                    className={
                      connectTarget === ''
                        ? 'text-sm px-3 py-1.5 rounded-full border border-[#1967D2] bg-[#1967D2] text-white disabled:opacity-70 disabled:cursor-default'
                        : 'text-sm px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground enabled:hover:border-[#1967D2] enabled:hover:text-foreground transition-colors disabled:opacity-70 disabled:cursor-default'
                    }
                  >
                    Andere Nummer
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {connectTarget
                    ? 'Meta bestätigt diese Nummer per Anruf oder SMS. Den Code zeigen wir dir hier an.'
                    : 'Eine Nummer außerhalb von TimeGlobe: Meta schickt den Code direkt an dieses ' +
                      'Telefon — hier können wir ihn nicht anzeigen.'}
                </p>
                {connectable.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Für eine Nummer von uns zuerst beim Telefonagenten eine weitere
                    Rufnummer hinzufügen — danach steht sie hier zur Auswahl.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={startWhatsAppConnect}
                  disabled={arming || connecting}
                  className="bg-[#1877F2] hover:bg-[#166fe5] text-white"
                >
                  {arming || connecting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <WhatsAppIcon className="h-4 w-4 mr-2" />
                  )}
                  {armedNumber ? 'Erneut mit Facebook verbinden' : 'Mit Facebook verbinden'}
                </Button>
                <Button variant="ghost" onClick={cancelConnect} disabled={connecting}>
                  Abbrechen
                </Button>
              </div>

              {armedNumber && (
                <WhatsAppVerification
                  twilioPurchasedNumber={armedNumber}
                  scopeNumber={armedNumber}
                  numberType="local"
                  allowMethodSwitch
                  autoConfigureWebhook={false}
                  title="Verifizierungscode"
                  description={`Meta bestätigt ${armedNumber} — der Code erscheint hier, sobald der Anruf oder die SMS eingeht.`}
                />
              )}
            </div>
          )}
        </div>
      )}

      {showBuy && (
        <div className="pt-4 border-t border-border space-y-4">
          {!buyOpen ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setBuyOpen(true);
                  handleSearch(buyCountry);
                }}
                className="border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Weitere Rufnummer hinzufügen
              </Button>
              <span className="text-xs text-muted-foreground">
                Neue Nummer buchen und direkt Salons zuordnen — die bestehende
                Hauptnummer bleibt unverändert.
              </span>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    Land
                  </Label>
                  <Select
                    value={buyCountry}
                    onValueChange={(v) => {
                      const country = v as 'DE' | 'CH' | 'AT';
                      setBuyCountry(country);
                      handleSearch(country);
                    }}
                  >
                    <SelectTrigger className="w-40 bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DE">Deutschland</SelectItem>
                      <SelectItem value="CH">Schweiz</SelectItem>
                      <SelectItem value="AT">Österreich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" onClick={() => setBuyOpen(false)}>
                  Abbrechen
                </Button>
              </div>

              {buySearching ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Freie Nummern werden
                  gesucht…
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {buyCandidates.map((n) => (
                    <button
                      key={n.phone_number}
                      type="button"
                      onClick={() => setBuySelected(n.phone_number)}
                      className={
                        buySelected === n.phone_number
                          ? 'font-mono text-sm px-3 py-1.5 rounded-full border border-[#1967D2] bg-[#1967D2] text-white'
                          : 'font-mono text-sm px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground hover:border-[#1967D2] hover:text-foreground transition-colors'
                      }
                    >
                      {n.phone_number}
                    </button>
                  ))}
                </div>
              )}

              <Button
                onClick={handleBuy}
                disabled={buying || !buySelected}
                className="bg-[#1967D2] hover:bg-[#1558b0]"
              >
                {buying ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Phone className="h-4 w-4 mr-2" />
                )}
                Nummer reservieren
              </Button>

              {/* CH/AT need the customer's own approved regulatory bundle —
                  say so instead of failing at purchase. */}
              {buyCountry !== 'DE' && (
                <p className="text-xs text-warning-strong">
                  Für die Schweiz und Österreich muss der Regulierungs-Nachweis dieses
                  Kontos freigegeben sein, sonst lehnt Twilio den Kauf ab.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentNumberManager;
